import { VERZEICHNIS } from './data/ortschaften.js'

/**
 * Die Ortssuche: eine Postleitzahl rein, ein Ortsname raus.
 *
 * Eine Stelle für alle Adressfelder der Anwendung. Vorher rechnete sich der
 * Rechnungsbildschirm seine Vorschlagsliste selbst zusammen, und die
 * Kunden-, Projekt- und Einstellungsmasken hatten gar keine – dieselbe
 * Postleitzahl also je nach Bildschirm mit Vorschlag oder ohne. Das ist
 * genau die Art Unterschied, die niemand meldet und über die sich alle
 * ärgern.
 *
 * Zwei Quellen, und ihre Reihenfolge ist die ganze Idee:
 *
 *   1. Der eigene Bestand. Was der Betrieb selbst erfasst hat, zählt mehr als
 *      jedes Verzeichnis – wer seine Kunden in «Zürich-Altstetten» führt, soll
 *      «Zürich-Altstetten» vorgeschlagen bekommen und nicht «Zürich».
 *   2. Das amtliche Verzeichnis. Es füllt die Lücken: eine Postleitzahl, die
 *      der Betrieb noch nie erfasst hat, ist heute ein leeres Feld und morgen
 *      ein Vorschlag.
 *
 * Zerlegt wird beim ersten Zugriff und danach nie wieder. Beim Laden der Seite
 * passiert hier nichts.
 */

export interface Ortschaft {
  plz: string
  ort: string
  /** Aus dem eigenen Bestand – wird zuerst vorgeschlagen. */
  eigen: boolean
}

let amtlich: Array<[string, string]> | null = null

function amtlicheListe(): Array<[string, string]> {
  if (amtlich) return amtlich
  amtlich = VERZEICHNIS
    ? VERZEICHNIS.split('|').map((eintrag) => {
        const trenner = eintrag.indexOf(':')
        return [eintrag.slice(0, trenner), eintrag.slice(trenner + 1)] as [string, string]
      })
    : []
  return amtlich
}

/** Steht ein amtliches Verzeichnis zur Verfügung? Entscheidet den Hinweistext. */
export function verzeichnisVorhanden(): boolean {
  return amtlicheListe().length > 0
}

/**
 * Die Ortschaften aus dem eigenen Bestand.
 *
 * Nimmt die Zeilen, wie sie im lokalen Zwischenspeicher liegen – Kunden,
 * Projekte, was sonst noch eine Adresse trägt. Was keine Postleitzahl und
 * keinen Ort hat, fällt weg; ein halbes Paar hilft niemandem.
 */
export function eigeneOrtschaften(
  ...tabellen: Array<ReadonlyArray<Record<string, unknown>>>
): Array<[string, string]> {
  const paare = new Map<string, string>()
  for (const zeilen of tabellen) {
    for (const z of zeilen) {
      const plz = String(z.zip ?? '').trim()
      const ort = String(z.city ?? '').trim()
      if (plz && ort && !paare.has(plz)) paare.set(plz, ort)
    }
  }
  return [...paare.entries()]
}

/**
 * Vorschläge zu einer angefangenen Eingabe.
 *
 * Gesucht wird von vorne und nicht irgendwo mittendrin: wer «86» tippt, meint
 * die Achtsechstausender und nicht die 3086. Ohne Eingabe kommt der eigene
 * Bestand – das ist die Liste, die ein Betrieb im Normalfall braucht.
 *
 * Gesucht wird in BEIDEN Spalten, und das ist keine Bequemlichkeit: Wer eine
 * Rechnung schreibt, weiss meistens den Ort und nicht die Postleitzahl. Ein
 * Feldpaar, das nur auf Ziffern reagiert, verlangt vom Betrieb genau das
 * Wissen, das die Anwendung eigentlich beisteuern sollte.
 */
export function vorschlaege(
  eingabe: string,
  eigen: ReadonlyArray<[string, string]>,
  grenze = 12
): Ortschaft[] {
  const suche = eingabe.trim()
  const klein = suche.toLocaleLowerCase('de-CH')
  const gesehen = new Set<string>()
  const treffer: Ortschaft[] = []

  const passt = (plz: string, ort: string): boolean =>
    plz.startsWith(suche) || ort.toLocaleLowerCase('de-CH').startsWith(klein)

  const nimm = (plz: string, ort: string, ausEigenem: boolean): void => {
    const schluessel = `${plz} ${ort}`
    if (gesehen.has(schluessel)) return
    gesehen.add(schluessel)
    treffer.push({ plz, ort, eigen: ausEigenem })
  }

  for (const [plz, ort] of eigen) {
    if (!suche || passt(plz, ort)) nimm(plz, ort, true)
  }
  if (suche) {
    for (const [plz, ort] of amtlicheListe()) {
      if (treffer.length >= grenze) break
      if (passt(plz, ort)) nimm(plz, ort, false)
    }
  }
  return treffer.slice(0, grenze)
}

/**
 * Der Ort zu einer vollständigen Postleitzahl, oder nichts.
 *
 * «Vollständig» heisst vier Stellen. Bei drei Stellen wäre jede Antwort
 * geraten, und ein geratener Ort, der sich beim Weitertippen wieder ändert,
 * ist schlimmer als ein leeres Feld.
 */
export function ortZuPlz(plz: string, eigen: ReadonlyArray<[string, string]>): string | null {
  const wert = plz.trim()
  if (!/^\d{4}$/.test(wert)) return null
  const ausEigenem = eigen.find(([p]) => p === wert)
  if (ausEigenem) return ausEigenem[1]
  const ausVerzeichnis = amtlicheListe().find(([p]) => p === wert)
  return ausVerzeichnis ? ausVerzeichnis[1] : null
}

/**
 * Die Postleitzahl zu einem ausgeschriebenen Ort – aber nur, wenn sie eindeutig ist.
 *
 * «Eindeutig» ist die ganze Bedingung. Zu «Zürich» gehören über dreissig
 * Postleitzahlen; eine davon zu setzen wäre geraten, und geraten steht dann
 * auf der Rechnung. Zu «Wetzikon» gehört eine, und die darf folgen.
 *
 * Der eigene Bestand geht vor: Wer seine Kunden in Kloten führt, meint mit
 * «Kloten» die Postleitzahl, die er selbst schon benutzt hat.
 */
export function plzZuOrt(ort: string, eigen: ReadonlyArray<[string, string]>): string | null {
  const wert = ort.trim().toLocaleLowerCase('de-CH')
  if (wert.length < 2) return null

  const gleich = (o: string): boolean => o.toLocaleLowerCase('de-CH') === wert

  const ausEigenem = eigen.filter(([, o]) => gleich(o))
  if (ausEigenem.length === 1) return ausEigenem[0][0]

  const ausVerzeichnis = amtlicheListe().filter(([, o]) => gleich(o))
  return ausVerzeichnis.length === 1 ? ausVerzeichnis[0][0] : null
}

/**
 * Darf die Postleitzahl überschrieben werden?
 *
 * Dieselbe Regel wie beim Ort, nur andersherum: leer oder noch die Zahl, die
 * aus einem vorher getippten Ort kam. Eine von Hand eingetragene
 * Postleitzahl bleibt stehen.
 */
export function plzDarfFolgen(
  aktuellePlz: string,
  eigen: ReadonlyArray<[string, string]>
): boolean {
  const bisher = aktuellePlz.trim()
  if (bisher === '') return true
  if (eigen.some(([p]) => p === bisher)) return true
  return amtlicheListe().some(([p]) => p === bisher)
}

/**
 * Darf der Ort überschrieben werden?
 *
 * Nur wenn er leer ist oder noch der Ort einer vorher getippten Postleitzahl.
 * Wer den Ort bewusst anders schreibt, behält ihn – auch dann, wenn das
 * Verzeichnis es besser zu wissen meint.
 */
export function ortDarfFolgen(
  aktuellerOrt: string,
  eigen: ReadonlyArray<[string, string]>
): boolean {
  const bisher = aktuellerOrt.trim()
  if (bisher === '') return true
  if (eigen.some(([, o]) => o === bisher)) return true
  return amtlicheListe().some(([, o]) => o === bisher)
}

// ---------------------------------------------------------------------------
// Strassen
// ---------------------------------------------------------------------------

export interface Adresse {
  strasse: string
  plz: string
  ort: string
}

/**
 * Die Strassen aus dem eigenen Bestand.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum nur der eigene Bestand und nicht die ganze Schweiz
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Gefragt wurde nach einem Treffer schon bei der Strasse. Das amtliche
 * Verzeichnis dafür gibt es – das Gebäudeadressverzeichnis von swisstopo –
 * und es hat rund 2,4 Millionen Einträge. Das sind ein paar hundert Megabyte;
 * eingebaut in die Anwendung wie die 4363 Ortschaften geht das nicht. Es
 * gehört in die Datenbank, mit einem eigenen Serverweg, und das ist ein
 * eigener Schritt.
 *
 * Was ohne diesen Schritt geht, ist das hier: Ein Handwerksbetrieb arbeitet
 * in seiner Region. Nach ein paar Wochen steht die Hälfte der Strassen, die
 * er je tippen wird, schon bei seinen Kunden und Baustellen. Das kostet
 * nichts, funktioniert im Funkloch und trifft genau die Adressen, die dieser
 * Betrieb wirklich braucht.
 *
 * Mitgeliefert werden PLZ und Ort der Adresse: Wer die Strasse wählt, hat
 * meistens auch den Rest gemeint.
 */
export function eigeneStrassen(
  ...tabellen: Array<ReadonlyArray<Record<string, unknown>>>
): Adresse[] {
  const gesehen = new Map<string, Adresse>()
  for (const zeilen of tabellen) {
    for (const z of zeilen) {
      const strasse = String(z.street ?? '').trim()
      if (!strasse) continue
      const plz = String(z.zip ?? '').trim()
      const ort = String(z.city ?? '').trim()
      const schluessel = `${strasse}|${plz}|${ort}`.toLocaleLowerCase('de-CH')
      if (!gesehen.has(schluessel)) gesehen.set(schluessel, { strasse, plz, ort })
    }
  }
  return [...gesehen.values()].sort((a, b) => a.strasse.localeCompare(b.strasse, 'de-CH'))
}

/**
 * Strassenvorschläge zu einer angefangenen Eingabe.
 *
 * Gesucht wird von vorne UND an jeder Wortgrenze: Wer «bahnhof» tippt, meint
 * auch die «Alte Bahnhofstrasse». Mitten im Wort wird nicht gesucht – sonst
 * bringt «strasse» die halbe Liste zurück.
 */
export function strassenVorschlaege(
  eingabe: string,
  bestand: ReadonlyArray<Adresse>,
  grenze = 8
): Adresse[] {
  const suche = eingabe.trim().toLocaleLowerCase('de-CH')
  if (suche.length < 2) return []
  const treffer: Adresse[] = []
  for (const a of bestand) {
    const klein = a.strasse.toLocaleLowerCase('de-CH')
    if (klein.startsWith(suche) || klein.split(/[\s-]+/).some((w) => w.startsWith(suche))) {
      treffer.push(a)
      if (treffer.length >= grenze) break
    }
  }
  return treffer
}
